import React, {
  type ReactNode,
  createContext,
  useState,
  useEffect,
  useContext,
} from "react";
import {
  type Action,
  ItemObject,
  type Item,
  ExecutionContext,
  I18nContext,
  ItemType,
  type Hierarchical,
  type Section,
  type Row,
  type Matrix,
  type Group,
  type Text,
  DefaultCatalog,
  isQuestion,
} from "anketa-core";
import { getGroupTab } from "../group_card";
import { RowCard } from "../row_card";
import { QuestionCard } from "../question_card";
import { MatrixCard } from "../matrix_card";
import { getSectionTab } from "../section_card";
import { TextCard } from "../text_card";
import { ActionButton } from "../action_button";
import {
  EuiContext,
  EuiFlexItem,
  EuiSpacer,
  EuiTabbedContent,
  EuiTabbedContentTab,
} from "@elastic/eui";
import "../questionnaire-theme.css";
import { isCard } from "../presentation";

export const checkLocalStorageAccess = (): boolean => {
  try {
    const testKey = "__test__";
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

export const EvaluationContext = createContext({
  evaluationId: 0,
  nextEvaluationId: (): any => {},
  focusItem: "",
  setFocus: (itemPath: string): any => {},
  // When true, every field shows its validation state - not only the ones the
  // user already touched. Set by an explicit save or the issues summary.
  revealIssues: false,
  setRevealIssues: (reveal: boolean): any => {},
});

const draftKey = (catalog: Item): string => "anketa-" + catalog.id + "-facts";

export interface AnketaContextProviderProps {
  catalog: Item | undefined;
  locale: string;
  children: ReactNode;
}

export type ActionObserver = (action: Action) => void;
// export type FactObserver = (fact: Fact) => void

export class AnketaEuiContext extends ExecutionContext {
  i18n: any;
  private actionObservers: ActionObserver[] = [];

  constructor(catalog?: Item, locale?: string) {
    super(
      catalog && checkLocalStorageAccess()
        ? catalog.factsFromObject(
            localStorage.getItem(draftKey(catalog)),
          )
        : catalog,
      new I18nContext(locale),
    );
    this.locale = locale ?? I18nContext.Global.locale.baseName;
    this.reevaluate();
  }

  public attachActions(observer: ActionObserver): void {
    this.actionObservers.push(observer);
    console.debug("Attach Action", observer);
  }

  public detachActions(observerToRemove: ActionObserver): void {
    this.actionObservers = this.actionObservers.filter(
      (observer) => observerToRemove !== observer,
    );
  }

  private notifyAction(action: Action): void {
    this.actionObservers.forEach((observer) => {
      observer(action);
    });
  }

  public reevaluate(): void {
    super.evaluate();
  }

  public onAction(action: Action): void {
    console.debug("Action triggered", action.id);
    this.notifyAction(action);
  }

  public saveToBrowser(): void {
    if (this.catalog && this.catalog !== null) {
      const f = this.catalog.factsToObject(this.catalog);
      checkLocalStorageAccess() &&
        localStorage.setItem(draftKey(this.catalog), JSON.stringify(f));
    }
  }

  /**
   * Drop the local draft once the server holds the answers. A draft that
   * outlives a save would later be merged (by timestamp) over newer answers
   * saved from another browser.
   */
  public clearBrowserDraft(): void {
    if (this.catalog && checkLocalStorageAccess()) {
      localStorage.removeItem(draftKey(this.catalog));
    }
  }

  public loadFromBrowser(): void {
    if (this.catalog && this.catalog !== null) {
      if (checkLocalStorageAccess()) {
        const storedJSON = localStorage.getItem(draftKey(this.catalog));
        if (storedJSON) {
          const stored = JSON.parse(storedJSON);
          this.catalog.factsFromObject(stored);
        }
      }
    }
  }

  public get locale(): string {
    return this.i18nContext.locale.baseName;
  }

  public set locale(val: string) {
    this.i18nContext.locale = new Intl.Locale(val);
    this.i18n = {
      // mapping: language_mappings[language as keyof LanguageMapping],
      formatNumber: (value: number) => this.i18nContext.formatNumber(value),
    };
  }

  public componentForItem(item: Item): JSX.Element {
    return (
      <div>
        Unhandled item type: <pre>{JSON.stringify(item.save, null, 2)}</pre>
      </div>
    );
  }

  /**
   * Element for one non-tab child (row, matrix, text, action, question), or
   * null for sections/groups (they become tabs) and unknown items. Split out
   * of childrenForItem so a card Row can lay its children out itself.
   */
  public childElement(item: Item, child: Hierarchical): JSX.Element | null {
    if (!(child instanceof ItemObject)) {
      console.debug("[Debug](ERROR): No Valid Item");
      return null;
    }
    if (child.type === ItemType.Row) {
      if (item.type === ItemType.Row) {
        if (isCard(item)) {
          // Inside a card: a sub-panel spanning the card's field grid.
          return <RowCard key={child.path} row={child as Row} nested />;
        }
        return (
          <div
            key={child.path}
            style={{
              width: "100%",
              padding: "25px",
            }}
          >
            <RowCard row={child as Row}></RowCard>
          </div>
        );
      }
      return <RowCard key={child.path} row={child as Row}></RowCard>;
    }
    if (child.type === ItemType.Matrix) {
      return (
        <MatrixCard
          key={child.path}
          matrix={child as Matrix}
          stripes={true}
        ></MatrixCard>
      );
    }
    if (child.type === ItemType.Text) {
      return <TextCard key={child.path} text={child as Text}></TextCard>;
    }
    if (child.type === ItemType.Action) {
      return (
        <ActionButton key={child.path} action={child as Action}></ActionButton>
      );
    }
    if (isQuestion(child)) {
      return <QuestionCard key={child.path} question={child}></QuestionCard>;
    }
    if (child.type !== ItemType.Group && child.type !== ItemType.Section) {
      console.debug("[Debug](ERROR): No Valid Item!", child.type);
    }
    return null;
  }

  public childrenForItem(item: Item, rootPath?: string): JSX.Element[] {
    const result = new Array<JSX.Element>();
    const tabs: Array<EuiTabbedContentTab> = [];
    item.children.forEach((child: Hierarchical) => {
      if (child instanceof ItemObject && child.type === ItemType.Group) {
        if ((child as Group).visible) {
          tabs.push(
            getGroupTab({
              group: child as Group,
              parentId: item.id,
            }),
          );
        }
        return;
      }
      if (child instanceof ItemObject && child.type === ItemType.Section) {
        if ((child as Section).visible) {
          tabs.push(
            getSectionTab({
              section: child as Section,
              parentId: item.id,
            }),
          );
        }
        return;
      }
      const element = this.childElement(item, child);
      if (element) {
        result.push(element);
      }
    });
    if (tabs.length > 0) {
      // Top-level chapters get regular tabs; nested ones (e.g. the individual
      // scanners) get small tabs so the hierarchy reads at a glance.
      result.push(
        <EuiTabbedContent
          key={item.path + ".tabs"}
          tabs={tabs}
          size={item === this.catalog ? "m" : "s"}
        />,
      );
    }
    return result;
  }
}

export const AnketaContext = createContext<AnketaEuiContext>(
  new AnketaEuiContext(DefaultCatalog.instance, "en"),
);

export const AnketaContextProvider = ({
  catalog,
  locale,
  children,
}: AnketaContextProviderProps): JSX.Element => {
  const nextEvaluationId = (): any => {
    setEvaluationId((prevEvaluation: any): any => ({
      evaluationId: (prevEvaluation.evaluationId as number) + 1,
      nextEvaluationId,
    }));
  };
  const setFocus = (itemPath: string): any => {
    setFocusItem((prevPath: any): any => {
      return {
        focusItem: itemPath,
        setFocus,
      };
    });
  };

  const [ctx, setCtx] = useState<AnketaEuiContext | undefined>();
  const [evaluationId, setEvaluationId] = useState({
    evaluationId: 0,
    nextEvaluationId,
  });
  const [focusItem, setFocusItem] = useState({
    focusItem: ctx ? ctx.catalog.path : "/",
    setFocus,
  });
  const [revealIssues, setRevealIssues] = useState(false);

  useEffect(() => {
    if (!ctx) {
      setCtx(new AnketaEuiContext(catalog, locale));
    } else {
      if (
        locale.toLowerCase() !== ctx.i18nContext.locale.baseName.toLowerCase()
      ) {
        const newCtx = new AnketaEuiContext(catalog, locale);
        newCtx.catalog = ctx.catalog;
        setCtx(newCtx);
      } else if (catalog && catalog !== ctx.catalog) {
        // Compare by identity, not id: re-opening the same questionnaire loads a
        // fresh catalog object with the same id, and the editors must bind to
        // that one - not keep editing (and saving) the previous instance.
        setCtx(new AnketaEuiContext(catalog, locale));
        setRevealIssues(false);
      }
    }
  }, [ctx, catalog, locale]);

  if (!ctx || catalog === null) {
    return <></>;
  }
  return (
    <EvaluationContext.Provider
      value={{
        evaluationId: evaluationId.evaluationId,
        nextEvaluationId: evaluationId.nextEvaluationId,
        focusItem: focusItem.focusItem,
        setFocus: focusItem.setFocus,
        revealIssues,
        setRevealIssues,
      }}
    >
      <AnketaContext.Provider value={ctx}>
        <EuiContext i18n={ctx.i18n}>{children}</EuiContext>
      </AnketaContext.Provider>
    </EvaluationContext.Provider>
  );
};

export const useAnketa = (): AnketaEuiContext => {
  const context = useContext(AnketaContext);
  if (context === undefined) {
    throw new Error("useAnketa must be used within a AnketaContextProvider ");
  }
  return context as AnketaEuiContext;
};
